<template>
  <div class="fin-admin-page">
    <div v-if="!canAccess" class="fin-admin-card">
      <h1>Nuk keni te drejte</h1>
      <p>Ky panel eshte vetem per administratorët (type_id 1).</p>
    </div>

    <template v-else>
      <div class="fin-admin-hero">
        <div>
          <h1>Stok Financa — test</h1>
          <p>
            Mbush ose pastro tabelat e gjendjes analitike me te dhena false per testim UI.
            Kur API e Financa te jete live, pastro dhe ndize sinkronizimin.
          </p>
        </div>
        <router-link class="button is-light is-small" to="/stock/financa">Hap Gjendjen</router-link>
      </div>

      <div class="fin-admin-grid">
        <div class="fin-admin-card">
          <h2>Statusi aktual</h2>
          <div v-if="statusLoading" class="fin-admin-muted">Duke ngarkuar...</div>
          <template v-else>
            <dl class="fin-admin-stats">
              <div>
                <dt>Rreshta stok</dt>
                <dd>{{ status?.rows ?? 0 }}</dd>
              </div>
              <div>
                <dt>Sync i fundit</dt>
                <dd>{{ lastSyncLabel }}</dd>
              </div>
              <div>
                <dt>Trigger</dt>
                <dd>{{ lastSyncTrigger }}</dd>
              </div>
              <div>
                <dt>Produkte të përputhura</dt>
                <dd>{{ status?.matched?.products ?? 0 }} / {{ status?.fetched?.products ?? 0 }}</dd>
              </div>
              <div>
                <dt>Magazina të përputhura</dt>
                <dd>{{ status?.matched?.warehouses ?? 0 }} / {{ status?.fetched?.warehouses ?? 0 }}</dd>
              </div>
              <div>
                <dt>Produkte pa mapim</dt>
                <dd>{{ status?.unmatched?.products ?? 0 }}</dd>
              </div>
              <div>
                <dt>Magazina pa mapim</dt>
                <dd>{{ status?.unmatched?.warehouses ?? 0 }}</dd>
              </div>
              <div>
                <dt>Mbyllja e fundit e furnizimeve</dt>
                <dd>{{ lastCloseLabel }}</dd>
              </div>
            </dl>
            <button type="button" class="button is-small is-light" :disabled="busy" @click="refreshStatus">Rifresko statusin</button>
          </template>
        </div>

        <div class="fin-admin-card">
          <h2>Veprime</h2>
          <p class="fin-admin-warn">
            Seed fshin stokun ekzistues dhe e zevendeson me te dhena false.
            Clear e boshatis plotesisht — pastaj pret sync nga Financa.
          </p>
          <div class="fin-admin-actions">
            <button
              type="button"
              class="button is-link"
              :disabled="busy"
              @click="runSeed"
            >
              {{ seeding ? "Duke mbushur..." : "Mbush me stok false" }}
            </button>
            <button
              type="button"
              class="button is-danger is-light"
              :disabled="busy"
              @click="runClear"
            >
              {{ clearing ? "Duke pastruar..." : "Pastro stokun" }}
            </button>
          </div>
          <p v-if="message" class="fin-admin-msg" :class="{ error: messageError }">{{ message }}</p>
          <pre v-if="lastResult" class="fin-admin-result">{{ lastResult }}</pre>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import FinancaStock from "@/services/FinancaStock";

export default {
  name: "FinancaStockAdmin",
  data() {
    return {
      canAccess: false,
      status: null,
      statusLoading: false,
      seeding: false,
      clearing: false,
      message: "",
      messageError: false,
      lastResult: "",
    };
  },
  computed: {
    busy() {
      return this.seeding || this.clearing || this.statusLoading;
    },
    lastSyncLabel() {
      const sync = this.status?.last_sync;
      if (!sync?.finished_at && !sync?.started_at) return "—";
      return sync.finished_at || sync.started_at;
    },
    lastSyncTrigger() {
      const recent = this.status?.recent_syncs?.[0];
      if (!recent) return "—";
      return `${recent.trigger || "?"} · ${recent.status || "?"}`;
    },
    lastCloseLabel() {
      const close = this.status?.last_day_close;
      if (!close?.closed_on) return "—";
      return `${close.closed_on} · ${close.status || "?"}`;
    },
  },
  created() {
    this.canAccess = this.isFinancaStockAdmin();
    if (!this.canAccess) return;
    this.refreshStatus();
  },
  methods: {
    isFinancaStockAdmin() {
      try {
        const user = JSON.parse(localStorage.user || "null");
        const typeId = Number(user?.type_id ?? 0);
        return typeId === 1 || typeId === -1;
      } catch {
        return false;
      }
    },
    async refreshStatus() {
      this.statusLoading = true;
      this.message = "";
      try {
        this.status = await FinancaStock.getStatus();
      } catch (e) {
        this.messageError = true;
        this.message = e?.response?.data?.message || "Nuk u ngarkua statusi.";
      } finally {
        this.statusLoading = false;
      }
    },
    async runSeed() {
      if (!confirm("Ke siguri? Stoku aktual i Financa do te fshihet dhe do te zevendesohet me te dhena false. Mund te zgjase shume.")) {
        return;
      }
      this.seeding = true;
      this.message = "";
      this.messageError = false;
      this.lastResult = "";
      try {
        const result = await FinancaStock.seedFake();
        this.lastResult = JSON.stringify(result, null, 2);
        this.message = `U mbush: ${result.pairs_written ?? 0} pale stok, ${result.movements_written ?? 0} levizje.`;
        await this.refreshStatus();
      } catch (e) {
        this.messageError = true;
        this.message = e?.response?.data?.message || "Seed deshtoi.";
      } finally {
        this.seeding = false;
      }
    },
    async runClear() {
      if (!confirm("Ke siguri? Do te boshatisen te gjitha tabelat financa_stock_*. Pastaj pret sync live.")) {
        return;
      }
      this.clearing = true;
      this.message = "";
      this.messageError = false;
      this.lastResult = "";
      try {
        const result = await FinancaStock.clearFake();
        this.lastResult = JSON.stringify(result, null, 2);
        this.message = "Stoku u pastrua.";
        await this.refreshStatus();
      } catch (e) {
        this.messageError = true;
        this.message = e?.response?.data?.message || "Clear deshtoi.";
      } finally {
        this.clearing = false;
      }
    },
  },
};
</script>

<style scoped>
.fin-admin-page {
  padding: 16px;
  background: linear-gradient(180deg, #f8fbff 0%, #f3f6fb 100%);
  min-height: calc(100vh - 64px);
}
.fin-admin-hero {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.fin-admin-hero h1 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
  color: #111827;
}
.fin-admin-hero p {
  margin: 0;
  max-width: 640px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.45;
}
.fin-admin-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
}
.fin-admin-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
}
.fin-admin-card h1,
.fin-admin-card h2 {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}
.fin-admin-muted {
  color: #6b7280;
  font-size: 13px;
}
.fin-admin-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 0 0 12px;
}
.fin-admin-stats dt {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9ca3af;
}
.fin-admin-stats dd {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.fin-admin-warn {
  margin: 0 0 12px;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #9a3412;
  font-size: 13px;
  line-height: 1.4;
}
.fin-admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.fin-admin-msg {
  margin: 12px 0 0;
  font-size: 13px;
  color: #047857;
}
.fin-admin-msg.error {
  color: #b91c1c;
}
.fin-admin-result {
  margin: 12px 0 0;
  padding: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
